/**
 * Remote Speakers category: the current section's remote speaker consoles with a camera preview, and the
 * on-stage toggle that brings them all in. There is no per-speaker selection.
 */

import { useMemo } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import { useGetPlayersQuery } from '@/state/queries/players/use-get-players';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';
import { useLiveContextStateWithLivePlayState } from '@/hooks/use-live-play-state';
import { resolveLiveBindingFlowId } from '@/helpers/object.id.helper';
import { remoteSpeakersForSection } from '@/helpers/remote-speaker-players.helper';
import { useTranslations } from '@/i18n/use-translations';
import { LiveKitPreviewConnector } from '@/components/preview/livekit-preview-connector';
import { RemoteSpeakersOnStageToggle } from './remote-speakers-on-stage-toggle';

export function PerformRemoteSpeakersPanel({
  flow,
  entityRef,
  eventSlotId,
  livekitRoomId,
  liveContextState,
}: {
  flow: FlowDto;
  entityRef?: string;
  eventSlotId?: string;
  livekitRoomId?: string;
  liveContextState?: LiveContextStateDto;
}) {
  const t = useTranslations();
  const bindingFlowId = resolveLiveBindingFlowId(flow, eventSlotId);
  const liveReady = flow?.liveContext?.isRunning ?? false;
  const { data: players = [] } = useGetPlayersQuery(
    bindingFlowId
      ? {
          entityId: bindingFlowId,
          entityType: 'Flow',
          ...(eventSlotId ? { eventSlotId } : {}),
        }
      : undefined,
    { enabled: !!bindingFlowId && liveReady },
  );
  const cueState = useLiveContextCueState(entityRef, undefined, liveContextState?.cueState);
  const speakers = useMemo(
    () => remoteSpeakersForSection(players, cueState?.activeSlotId),
    [players, cueState?.activeSlotId],
  );
  const speakersOnStage =
    useLiveContextStateWithLivePlayState(entityRef, liveContextState)?.remoteSpeakersOnStage === true;

  if (!livekitRoomId || !entityRef) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        {t('flows.detail.perform.categoryModal.noLiveContext', {
          defaultValue: 'Live context not available. Start the flow to connect.',
        })}
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <RemoteSpeakersOnStageToggle
          flow={flow}
          entityRef={entityRef}
          eventSlotId={eventSlotId}
          liveContextState={liveContextState}
        />
        <Typography variant="body2" color="text.secondary">
          {t('flows.detail.perform.remoteSpeakersOnStage.hint')}
        </Typography>
      </Stack>
      {speakers.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('flows.detail.perform.remoteSpeakersOnStage.noneInSection')}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
          {speakers.map((player) => {
            const onStage = speakersOnStage;
            return (
              <Paper
                key={player.id}
                elevation={onStage ? 4 : 1}
                sx={{
                  overflow: 'hidden',
                  outline: onStage ? '2px solid' : 'none',
                  outlineColor: 'error.main',
                }}
              >
                <Box sx={{ bgcolor: '#000', width: '100%', aspectRatio: '16/9' }}>
                  <LiveKitPreviewConnector
                    livekitRoomId={livekitRoomId}
                    mode="preview"
                    publisherPlayerId={player.id}
                    showCamera
                    showControls={false}
                    height="100%"
                  />
                </Box>
                <Typography variant="body2" sx={{ px: 1, py: 0.75 }}>
                  {player.name ?? player.id}
                </Typography>
              </Paper>
            );
          })}
        </Box>
      )}
    </Stack>
  );
}
